import { useCallback, useState } from 'react';
import Video from 'twilio-video';
import { Logger } from '../../utils';
import { useTwilioAppState } from '../../state';
import { SELECTED_AUDIO_INPUT_KEY } from '../../constants';
import useDevices from '../../hooks/useDevices';

export default function useLocalTracks(onError) {
    const [audioTrack, setAudioTrack] = useState();
    const [videoTrack, setVideoTrack] = useState();
    const [isAcquiringLocalTracks, setIsAcquiringLocalTracks] = useState(false);
    const [isAcquiringLocalTracksCompleted, setIsAcquiringLocalTracksCompleted] = useState(false);
    const { settings, _isMounted, twilioSettings } = useTwilioAppState();
    const { audioInputDevices, videoInputDevices, hasAudioInputDevices, hasVideoInputDevices, isMediaDeviceValidated } = useDevices();
    //const localAudioDevices = audioInputDevices;
    let videoConstraints = null;
    if (twilioSettings && twilioSettings.settings) {
        videoConstraints = twilioSettings.settings.videoConstraints ? twilioSettings.settings.videoConstraints : null;
        videoConstraints = twilioSettings.settings.video ? twilioSettings.settings.video : videoConstraints;
    }
    else {
        videoConstraints = settings && settings.videoConstraints ? settings.videoConstraints : null;
        videoConstraints = settings && settings.video ? settings.video : videoConstraints;
    }

    const DEFAULT_VIDEO_CONSTRAINTS = {
        width: videoConstraints && videoConstraints.width ? videoConstraints.width : 1280,
        height: videoConstraints && videoConstraints.height ? videoConstraints.height : 720,
        frameRate: videoConstraints && videoConstraints.frameRate ? videoConstraints.frameRate : 24
    };

    const getLocalAudioTrack = useCallback(deviceId => {
        const options = {};

        if (deviceId) {
            options.deviceId = { exact: deviceId };
        }

        return Video.createLocalAudioTrack(options).then(newTrack => {
            if (_isMounted && _isMounted.current === true)
                setAudioTrack(newTrack);
            return newTrack;
        });
    }, [_isMounted]);

    const getLocalVideoTrack = useCallback(newOptions => {
        // In the DeviceSelector and FlipCameraButton components, a new video track is created,
        // then the old track is unpublished and the new track is published. Unpublishing the old
        // track and publishing the new track at the same time sometimes causes a conflict when the
        // track name is 'camera', so here we append a timestamp to the track name to avoid the
        // conflict.
        const options = {
            ...DEFAULT_VIDEO_CONSTRAINTS,
            name: `camera-${Date.now()}`,
            ...newOptions,
        };

        return Video.createLocalVideoTrack(options).then(newTrack => {
            if (_isMounted && _isMounted.current === true)
                setVideoTrack(newTrack);
            return newTrack;
        });
    }, [_isMounted]);

    const removeLocalAudioTrack = useCallback(() => {
        if (audioTrack) {
            audioTrack.stop();
            setAudioTrack(undefined);
        }
    }, [audioTrack]);

    const removeLocalVideoTrack = useCallback(() => {
        if (videoTrack) {
            videoTrack.stop();
            if (_isMounted && _isMounted.current === true)
                setVideoTrack(undefined);
        }
    }, [videoTrack, _isMounted]);

    const getAudioAndVideoTracks = useCallback(() => {
        if (!hasAudioInputDevices && !hasVideoInputDevices && isMediaDeviceValidated) {
            Logger("UI.Twilio.Room.NoDevicesFound", new Date());
            return Promise.resolve();
        }
        if (isAcquiringLocalTracks || audioTrack || videoTrack) return Promise.resolve("Tracks Already Acquired");

        setIsAcquiringLocalTracks(true);

        const selectedAudioDeviceId = window.localStorage.getItem(SELECTED_AUDIO_INPUT_KEY);

        const hasSelectedAudioDevice = audioInputDevices.some(
            device => selectedAudioDeviceId && device.deviceId === selectedAudioDeviceId
        );

        const localTrackConstraints = {
            video: {
                ...DEFAULT_VIDEO_CONSTRAINTS,
                name: `camera-${Date.now()}`,
            },
            audio: hasSelectedAudioDevice && selectedAudioDeviceId ? { deviceId: { exact: selectedAudioDeviceId } } : true,
        };

        return Video.createLocalTracks(localTrackConstraints)
            .then(tracks => {
                const videoTrack = tracks.find(track => track.kind === 'video');
                const audioTrack = tracks.find(track => track.kind === 'audio');
                if (videoTrack) {
                    setVideoTrack(videoTrack);
                }
                if (audioTrack) {
                    setAudioTrack(audioTrack);
                }
                return Promise.resolve(tracks);
            })
            .finally(() => {
                setIsAcquiringLocalTracks(false);
                setIsAcquiringLocalTracksCompleted(true);
            });
    }, [
        hasAudioInputDevices,
        hasVideoInputDevices,
        audioTrack,
        videoTrack,
        audioInputDevices,
        videoInputDevices,
        isAcquiringLocalTracks,
        isAcquiringLocalTracksCompleted
    ]);

    const localTracks = [audioTrack, videoTrack].filter(track => track !== undefined);

    return { localTracks, getLocalVideoTrack, getLocalAudioTrack, isAcquiringLocalTracks, removeLocalVideoTrack, removeLocalAudioTrack, getAudioAndVideoTracks, isAcquiringLocalTracksCompleted };
}
